import React, { useRef } from "react";
import { connect } from "react-redux";
import { Formik } from "formik";
import { Form } from "react-bootstrap";

import * as notifications from "notifications";
import {
  Select,
  FormCollapsible,
  SubmitButton,
  TextInput,
  CurrencyInput,
  StateSelect,
  DateInput,
  PercentageInput,
  NumericInput,
} from "components/forms";
import {
  copyCase,
  copyInsured,
  createEmptyCase,
  dataTape,
  baseXML,
  cgFile,
  pricingAndDataTape,
  pricingSetup,
  fetchInsured,
  runWinFlexIllustration,
  fetchWinFlexChoices,
  lilac,
} from "actions";
import {
  activeCaseIdSelector,
  activeCaseSelector,
  carriersSelector,
  insuredListSelector,
  policySelector,
} from "reducers";
import { makeChoices, moneyDisplay } from "../../../utils";
import { genderChoices } from "../../../options";

const PRICING = "Pricing";
const LIFEROC_DATA_TAPE = "LifeRoc Data Tape";
const PRICING_AND_LIFEROC = "Pricing & LifeRoc Data Tape";
const LILAC = "LILAC Pricing Model";
const CG_DATA_IMPORT = "CG Data Import";
const RESS_XML = "Ress XML";
const SL_XML = "SL XML";
const BROADRIVER_XML = "BroadRiver XML";

const DOCUMENT_OPTIONS = [
  PRICING,
  LIFEROC_DATA_TAPE,
  PRICING_AND_LIFEROC,
  LILAC,
  CG_DATA_IMPORT,
  RESS_XML,
  SL_XML,
  BROADRIVER_XML,
];

const PricingDocumentForm = ({ id, ...props }) => {
  const formikRef = useRef();

  React.useEffect(() => {
    // Required for WinFlex details
    props.fetchInsured(props.caseInfo.id);
    props.fetchWinFlexChoices();
  }, [props.caseInfo?.id]);

  let face_amount = props.caseInfo?.face_amount;
  if (face_amount) {
    face_amount = moneyDisplay(face_amount).replace("$", "");
  }
  let initialValues = {
    show_error: "Data tape already exists; data may be lost.",
    state: "WA",
    sex: props.insuredInfo?.[0]?.gender,
    dob: props.insuredInfo?.[0]?.date_of_birth,
    face_amount: face_amount,
    offer: 0,
    annuity_amount: face_amount,
    servicing_fee: 75,
    assumed_le: 145,
    midpoint_irr: 0.0755,
    number_of_insured_lives: 1,
  };
  if (props.insuredInfo?.[1]) {
    // If one of the insureds is deceased, use the other as the single insured
    const firstInsured = props.insuredInfo?.[0];
    const secondInsured = props.insuredInfo?.[1];
    if (secondInsured.insured_status === "D") {
      initialValues.sex = props.insuredInfo[0].gender;
      initialValues.dob = props.insuredInfo[0].date_of_birth;
    } else if (firstInsured.insured_status === "D") {
      initialValues.sex = props.insuredInfo[1].gender;
      initialValues.dob = props.insuredInfo[1].date_of_birth;
    } else {
      initialValues.number_of_insured_lives = 2;
      initialValues.sex2 = props.insuredInfo[1].gender;
      initialValues.dob2 = props.insuredInfo[1].date_of_birth;
    }
  }

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={async (values, { setSubmitting, setErrors }) => {
        delete values.show_error;
        if (values.action) {
          try {
            switch (values.action) {
              case PRICING:
                await props.pricingSetup(id);
                break;
              case LIFEROC_DATA_TAPE:
                await props.dataTape(id);
                break;
              case PRICING_AND_LIFEROC:
                await props.pricingAndDataTape(id);
                break;
              case LILAC: {
                let data = { ...values };
                if (data.number_of_insured_lives === 1) {
                  delete data.sex2;
                  delete data.dob2;
                }
                await props.lilac(id, data);
                break;
              }
              case CG_DATA_IMPORT:
                await props.cgFile(id);
                break;
              case RESS_XML:
                await props.baseXML(id, "ress-xml");
                break;
              case SL_XML:
                await props.baseXML(id, "sl-xml");
                break;
              case BROADRIVER_XML:
                await props.baseXML(id, "broadriver-xml");
                break;
            }
          } catch (error) {
            setErrors(error.response.data);
            notifications.error("Error generating document");
          }
          setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, values, setFieldValue }) => {
        function updateAnnuityField(updatedField, value) {
          let offer_value = values.offer || "0";
          let face_amount_value = values.face_amount || props.caseInfo?.face_amount;
          if (updatedField === "offer") {
            offer_value = value;
          } else if (updatedField === "face_amount") {
            face_amount_value = value;
          }
          face_amount_value = parseFloat(face_amount_value.replace(/,/g, ""));
          offer_value = parseFloat(offer_value.replace(/,/g, ""));
          let annuity_amount = face_amount_value - offer_value;
          if (annuity_amount) {
            annuity_amount = moneyDisplay(annuity_amount).replace("$", "");
            setFieldValue("annuity_amount", annuity_amount);
          } else {
            setFieldValue("annuity_amount", 0);
          }
        }
        function renderLILACRows() {
          return (
            <>
              <Form.Row>
                <Select
                  label="Insured Lives"
                  name="number_of_insured_lives"
                  placeholder="Select Insured Lives"
                  options={[
                    { value: 1, label: "Single" },
                    { value: 2, label: "Joint Survivor" },
                  ]}
                />
              </Form.Row>
              <hr />
              <Form.Row>
                <StateSelect label="State" name="state" placeholder="Select State" required={true} />
              </Form.Row>
              <Form.Row>
                <Select
                  placeholder="Select Gender"
                  label="Insured 1 Gender"
                  name="sex"
                  options={genderChoices.getChoices()}
                  required={true}
                />
                <DateInput label="Insured 1 DOB" name="dob" required={true} />
              </Form.Row>
              {values.number_of_insured_lives === 2 && (
                <Form.Row>
                  <Select
                    placeholder="Select Gender"
                    label="Insured 2 Gender"
                    name="sex2"
                    options={genderChoices.getChoices()}
                    required={true}
                  />
                  <DateInput label="Insured 2 DOB" name="dob2" required={true} />
                </Form.Row>
              )}
              <Form.Row>
                <CurrencyInput label="Face Amount" name="face_amount" onChangeCallback={updateAnnuityField} />
                <CurrencyInput label="Offer" name="offer" onChangeCallback={updateAnnuityField} />
              </Form.Row>
              <Form.Row>
                <CurrencyInput label="Annuity" name="annuity_amount" disabled />
                <PercentageInput label="Mid Point IRR" name="midpoint_irr" required={true} />
              </Form.Row>
              <Form.Row>
                <CurrencyInput label="Servicing Fee" name="servicing_fee" />
                <CurrencyInput label="Origination Fee" name="origination_fee" />
              </Form.Row>
              <Form.Row>
                <CurrencyInput label="Add Monthly Expense" name="monthly_expense" />
                <CurrencyInput label="Adjustment" name="adjustment" />
              </Form.Row>
              <Form.Row>
                <CurrencyInput label="Level Premium/mo." name="level_premium_per_month" />
                <NumericInput label="Assumed LE (Months)" name="assumed_le" required={true} />
              </Form.Row>
              <br />
            </>
          );
        }

        return (
          <FormCollapsible onSubmit={handleSubmit} title="Generate Pricing Documents">
            <Form.Row>
              <Select
                label="Selected Document"
                name="action"
                placeholder="Select a Document"
                options={props.documentChoices}
                expandMenu={true}
              />
            </Form.Row>
            {values.action === LILAC && renderLILACRows()}
            {[PRICING_AND_LIFEROC, CG_DATA_IMPORT].includes(values.action) && (
              <Form.Row>
                <TextInput label="Warning" name="show_error" disabled />
              </Form.Row>
            )}
            <SubmitButton
              defaultText={"Generate"}
              disabled={!values.action}
              style={{
                width: "90%",
                marginBottom: "1rem",
              }}
            />
          </FormCollapsible>
        );
      }}
    </Formik>
  );
};

const mapStateToProps = state => {
  const documentChoices = makeChoices(DOCUMENT_OPTIONS);
  return {
    caseInfo: activeCaseSelector(state),
    activeCaseId: activeCaseIdSelector(state),
    carriers: carriersSelector(state),
    policyInfo: policySelector(state),
    insuredInfo: insuredListSelector(state),
    documentChoices,
  };
};

export default connect(mapStateToProps, {
  copyCase,
  copyInsured,
  createEmptyCase,
  pricingSetup,
  dataTape,
  cgFile,
  baseXML,
  runWinFlexIllustration,
  pricingAndDataTape,
  lilac,
  fetchInsured,
  fetchWinFlexChoices,
})(PricingDocumentForm);
